import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const CampaignTreeSidebar = ({ onCampaignSelect, selectedCampaignId }) => {
  const [expandedNodes, setExpandedNodes] = useState(new Set(['auto-insurance', 'home-insurance']));
  const [searchQuery, setSearchQuery] = useState('');

  const campaignData = [
    {
      id: 'auto-insurance',
      name: 'Auto Insurance',
      type: 'category',
      status: 'active',
      children: [
        {
          id: 'ai-facebook-001',
          name: 'Facebook Auto Leads',
          type: 'campaign',
          status: 'active',
          callVolume: 847,
          revenue: 12450.75,
          eRPC: 14.70,
          winRate: 78.5,
          lastCall: '2 min ago'
        },
        {
          id: 'ai-google-002',
          name: 'Google Search Auto',
          type: 'campaign',
          status: 'active',
          callVolume: 623,
          revenue: 9876.25,
          eRPC: 15.85,
          winRate: 82.1,
          lastCall: '5 min ago'
        },
        {
          id: 'ai-tv-003',
          name: 'TV Commercial Auto',
          type: 'campaign',
          status: 'paused',
          callVolume: 234,
          revenue: 3245.50,
          eRPC: 13.87,
          winRate: 65.3,
          lastCall: '1 hour ago'
        }
      ]
    },
    {
      id: 'home-insurance',
      name: 'Home Insurance',
      type: 'category',
      status: 'active',
      children: [
        {
          id: 'hi-direct-001',
          name: 'Direct Mail Home',
          type: 'campaign',
          status: 'active',
          callVolume: 456,
          revenue: 7823.40,
          eRPC: 17.15,
          winRate: 89.2,
          lastCall: '1 min ago'
        },
        {
          id: 'hi-radio-002',
          name: 'Radio Home Leads',
          type: 'campaign',
          status: 'active',
          callVolume: 312,
          revenue: 4567.80,
          eRPC: 14.64,
          winRate: 73.8,
          lastCall: '8 min ago'
        }
      ]
    },
    {
      id: 'life-insurance',
      name: 'Life Insurance',
      type: 'category',
      status: 'active',
      children: [
        {
          id: 'li-social-001',
          name: 'Social Media Life',
          type: 'campaign',
          status: 'active',
          callVolume: 189,
          revenue: 2934.60,
          eRPC: 15.53,
          winRate: 76.4,
          lastCall: '12 min ago'
        }
      ]
    }
  ];

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded?.has(nodeId)) {
      newExpanded?.delete(nodeId);
    } else {
      newExpanded?.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-success';
      case 'paused':
        return 'text-warning';
      case 'stopped':
        return 'text-error';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return 'Play';
      case 'paused':
        return 'Pause';
      case 'stopped':
        return 'Square';
      default:
        return 'HelpCircle';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    })?.format(amount);
  };

  const filterCampaigns = (data, query) => {
    if (!query) return data;
    
    return data?.map(category => ({
      ...category,
      children: category?.children?.filter(campaign =>
        campaign?.name?.toLowerCase()?.includes(query?.toLowerCase())
      )
    }))?.filter(category => category?.children?.length > 0);
  };

  const filteredData = filterCampaigns(campaignData, searchQuery);

  return (
    <div className="h-full bg-card border-r border-border flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-foreground">Campaigns</h2>
          <button className="p-1 text-muted-foreground hover:text-foreground transition-smooth">
            <Icon name="Settings" size={16} />
          </button>
        </div>
        
        {/* Search */}
        <div className="relative">
          <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search campaigns..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="w-full pl-9 pr-3 py-2 bg-input border border-border rounded-lg text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      </div>
      {/* Campaign Tree */}
      <div className="flex-1 overflow-y-auto p-2">
        {filteredData?.map((category) => (
          <div key={category?.id} className="mb-2">
            {/* Category Node */}
            <button
              onClick={() => toggleNode(category?.id)}
              className="w-full flex items-center justify-between p-2 text-left hover:bg-muted rounded-lg transition-smooth"
            >
              <div className="flex items-center space-x-2">
                <Icon 
                  name={expandedNodes?.has(category?.id) ? "ChevronDown" : "ChevronRight"} 
                  size={16} 
                  className="text-muted-foreground" 
                />
                <Icon name="Folder" size={16} className="text-accent" />
                <span className="text-sm font-medium text-foreground">{category?.name}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name={getStatusIcon(category?.status)} size={12} className={getStatusColor(category?.status)} />
                <span className="text-xs text-muted-foreground">
                  {category?.children?.length}
                </span>
              </div>
            </button>

            {/* Campaign Children */}
            {expandedNodes?.has(category?.id) && (
              <div className="ml-4 mt-1 space-y-1">
                {category?.children?.map((campaign) => (
                  <button
                    key={campaign?.id}
                    onClick={() => onCampaignSelect(campaign)}
                    className={`w-full p-2 text-left rounded-lg transition-smooth ${
                      selectedCampaignId === campaign?.id
                        ? 'bg-primary/10 border border-primary/20' :'hover:bg-muted'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center space-x-2">
                        <Icon name="Phone" size={14} className="text-muted-foreground" />
                        <span className="text-sm font-medium text-foreground truncate">
                          {campaign?.name}
                        </span>
                      </div>
                      <Icon 
                        name={getStatusIcon(campaign?.status)} 
                        size={12} 
                        className={getStatusColor(campaign?.status)} 
                      />
                    </div>
                    
                    <div className="ml-6 space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Calls:</span>
                        <span className="text-foreground font-medium">{campaign?.callVolume}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Revenue:</span>
                        <span className="text-success font-medium">{formatCurrency(campaign?.revenue)}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">eRPC:</span>
                        <span className="text-foreground font-medium">${campaign?.eRPC}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Win Rate:</span>
                        <span className="text-accent font-medium">{campaign?.winRate}%</span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Last call: {campaign?.lastCall}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Footer Actions */}
      <div className="p-3 border-t border-border">
        <button className="w-full flex items-center justify-center space-x-2 p-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth">
          <Icon name="Plus" size={16} />
          <span className="text-sm font-medium">New Campaign</span>
        </button>
      </div>
    </div>
  );
};

export default CampaignTreeSidebar;